import { useEffect, useMemo, useState } from "react";
import { getFliptClient } from "utils/getFliptClient";

interface UseFeatureFlagOptions {
  context?: Record<string, string>;
  defaultValue?: boolean;
}

export enum FeatureFlagKeys {
  GAMES_BETTING_GR8 = "games_betting_gr8",
  PAYMENT_PROGRESS_BAR = "payment_progress_bar",
  COIN_FEST = "coin_fest",
}

export const useFeatureFlag = (
  flagKey: FeatureFlagKeys,
  userId: string,
  options: UseFeatureFlagOptions = {},
): boolean => {
  const [enabled, setEnabled] = useState<boolean>(
    options.defaultValue ?? false,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const contextKey = useMemo(
    () => JSON.stringify(options.context ?? {}),
    [options.context],
  );

  useEffect(() => {
    let isActive = true;

    const fetchFlag = async () => {
      try {
        const client = await getFliptClient();
        const result = client.evaluateBoolean({
          flagKey,
          entityId: userId || "guest",
          context: options.context ?? {},
        });

        if (isActive) {
          setEnabled(result.enabled ?? false);
        }
      } catch (err) {
        if (isActive) {
          setError(err as Error);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchFlag();

    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagKey, contextKey, userId]);

  if (loading || error) {
    return options.defaultValue ?? false;
  }

  return enabled;
};
